import '../styles/LandingHero.css';
import React, { useEffect, useRef, useState } from 'react';

/** Brushed-titanium macro plate. Supplied as a real direct HTTPS asset URL.
 *  If it fails to load, the dark titanium field, contour texture and amber arc
 *  remain and the type block plus both entry actions stay fully readable. */
const TITANIUM_PLATE_SRC =
  'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?auto=format&fit=crop&w=1600&q=70';

/* Contour-line texture path data (decorative, drawn behind the whole band) */
const CONTOUR_LINES = [
  'M-40 60 C 180 20, 320 130, 520 92 S 900 20, 1240 84 S 1620 150, 1960 96',
  'M-40 130 C 200 92, 360 200, 560 164 S 940 96, 1280 158 S 1660 216, 2000 166',
  'M-40 200 C 180 166, 340 268, 540 236 S 900 172, 1240 230 S 1620 288, 1960 240',
  'M-40 270 C 220 238, 380 336, 580 306 S 940 244, 1280 300 S 1660 356, 2000 310',
  'M-40 344 C 200 314, 360 406, 560 378 S 920 320, 1260 372 S 1640 428, 1980 384',
  'M-40 420 C 220 392, 380 480, 580 452 S 940 396, 1280 446 S 1660 500, 2000 458',
  'M-40 500 C 200 472, 360 556, 560 530 S 920 476, 1260 522 S 1640 574, 1980 534',
  'M-40 580 C 220 552, 380 632, 580 608 S 940 556, 1280 600 S 1660 648, 2000 612',
];

function LandingHero() {
  const plateRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  /* Mark the image ready only once it has decoded; the reveal + turntable
     drift run from CSS so reduced-motion can disable them in one place.
     A cached image that completed before hydration is handled explicitly. */
  useEffect(() => {
    const img = plateRef.current && plateRef.current.querySelector('.lh-plate__img');
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      setImgLoaded(true);
    }
  }, []);

  return (
    <section className="lh-root" aria-labelledby="lh-hero-title">
      {/* ---- faint topographic contour texture ---- */}
      <div className="lh-contour" aria-hidden="true">
        <svg viewBox="0 0 1600 640" preserveAspectRatio="xMidYMid slice">
          {CONTOUR_LINES.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </svg>
      </div>
      <div className="lh-vignette" aria-hidden="true" />

      <div className="lh-grid">
        {/* ================= TYPE BLOCK ================= */}
        <div className="lh-type">
          <p className="lh-mark lh-fade lh-fade--1">Online Banking System</p>

          <h1 className="lh-title lh-fade lh-fade--2" id="lh-hero-title">
            <span>Banking,</span>
            <span>Measured.</span>
          </h1>

          <p className="lh-lede lh-fade lh-fade--3">
            Open an account, check your balance, move funds and review your
            transfer history — read as instrument readings, not marketing.
          </p>

          <div className="lh-actions lh-fade lh-fade--4">
            <a className="lh-btn lh-btn--primary" href="/sign-up">
              Open an account
              <svg
                className="lh-btn__arrow"
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M0 6h14M9.5 1.5 14 6l-4.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a className="lh-btn lh-btn--ghost" href="/login">
              Sign in
            </a>
          </div>

          {/* instrument specimen readout — neutral, behavior-derived markings */}
          <div className="lh-readout lh-fade lh-fade--5">
            <div className="lh-readout__item">
              <span className="lh-readout__k">Account</span>
              <span className="lh-readout__v">Client-held</span>
            </div>
            <div className="lh-readout__item">
              <span className="lh-readout__k">Balance</span>
              <span className="lh-readout__v">Ledger ruled</span>
            </div>
            <div className="lh-readout__item">
              <span className="lh-readout__k">Transfers</span>
              <span className="lh-readout__v">Recorded</span>
            </div>
          </div>
        </div>

        {/* ================= TITANIUM PLATE ================= */}
        <div
          className={'lh-plate' + (imgFailed ? ' is-fallback' : '')}
          ref={plateRef}
        >
          <div className="lh-plate__frame">
            <div className="lh-plate__fallback" aria-hidden="true" />
            <img
              className={'lh-plate__img' + (imgLoaded ? ' is-ready' : '')}
              src={TITANIUM_PLATE_SRC}
              alt="Macro photograph of a brushed titanium watch bezel edge"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                setImgFailed(true);
                setImgLoaded(false);
              }}
            />

            {/* single thin amber arc — the only luminous gesture */}
            <svg
              className="lh-arc"
              viewBox="0 0 700 560"
              preserveAspectRatio="xMidYMid slice"
              aria-hidden="true"
            >
              <path
                className="lh-arc__trace"
                d="M -20 470 A 430 430 0 0 1 690 300"
                style={{ '--lh-arc-len': 1500 }}
              />
              <circle className="lh-arc__needle" cx="690" cy="300" r="4" />
            </svg>

            <span className="lh-plate__tick">
              <i aria-hidden="true" />
              Grade 5 Titanium
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingHero;